import React from "react";
import { Box, Typography } from "@mui/material";
import type { ComplianceSearchItem } from "@utils/complianceRequirementsSearch";
import {
  extractSearchSnippet,
  groupSearchResults,
  highlightSearchTerms,
} from "@utils/complianceRequirementsSearch";

export interface ComplianceSearchResultsProps {
  query: string;
  results: ComplianceSearchItem[];
  onResultClick: (item: ComplianceSearchItem) => void;
  textSearchLoading?: boolean;
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const segments = highlightSearchTerms(text, query);
  return (
    <>
      {segments.map((seg, i) =>
        seg.highlight ? (
          <Box
            key={i}
            component="mark"
            sx={{
              backgroundColor: "#FFF3B0",
              color: "inherit",
              padding: "0 1px",
            }}
          >
            {seg.text}
          </Box>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        ),
      )}
    </>
  );
}

function ComplianceSearchResultRow({
  item,
  query,
  onClick,
}: {
  item: ComplianceSearchItem;
  query: string;
  onClick: () => void;
}) {
  const snippet =
    item.apiSnippet ??
    extractSearchSnippet(item.snippetSource || item.title, query);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        padding: "12px 8px",
        borderBottom: "1px solid #e5e5e5",
        cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(153, 212, 225, 0.12)" },
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        sx={{
          fontFamily: "Rethink Sans, sans-serif",
          fontWeight: 500,
          fontSize: "15px",
          color: "#262626",
          marginBottom: snippet ? "4px" : 0,
        }}
      >
        <HighlightedText text={item.title} query={query} />
      </Typography>
      {snippet ? (
        <Typography
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: "13px",
            color: "#737373",
            lineHeight: 1.5,
          }}
        >
          <HighlightedText text={snippet} query={query} />
        </Typography>
      ) : null}
    </Box>
  );
}

export default function ComplianceSearchResults({
  query,
  results,
  onResultClick,
  textSearchLoading = false,
}: ComplianceSearchResultsProps) {
  const trimmed = query.trim();
  const groups = groupSearchResults(results);

  if (!trimmed) {
    return null;
  }

  if (results.length === 0 && !textSearchLoading) {
    return (
      <Box sx={{ padding: "24px 8px", textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: "15px",
            color: "#737373",
          }}
        >
          No results for &ldquo;{trimmed}&rdquo;
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{
          fontFamily: "Rethink Sans, sans-serif",
          fontSize: "14px",
          color: "#737373",
          marginBottom: "12px",
          paddingLeft: "4px",
        }}
      >
        {results.length} result{results.length === 1 ? "" : "s"} for &ldquo;{trimmed.toLowerCase()}&rdquo;
        {textSearchLoading ? " (searching regulation text…)" : ""}
      </Typography>

      {groups.map((group) => (
        <Box key={group.groupLabel} sx={{ marginBottom: "20px" }}>
          <Typography
            sx={{
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 600,
              fontSize: "13px",
              color: "#065b70",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              paddingBottom: "8px",
              borderBottom: "1px solid #99d4e1",
              marginBottom: "4px",
            }}
          >
            {group.groupLabel}
          </Typography>
          {group.items.map((item) => (
            <ComplianceSearchResultRow
              key={item.id}
              item={item}
              query={trimmed}
              onClick={() => onResultClick(item)}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}
